// Vstupni pole s napovedou nazvu materialu z backendu.
// Vola GET /api/material/suggest?q=<text>&limit=20 s 250ms debounce.
// Pri 404/501 (endpoint jeste neni implementovany) tise spadne na obycejny
// input bez napovedy.

import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MaterialAutocomplete({
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: Props) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  // Endpoint je optional — pokud spadne, prestaneme se ho ptat.
  const [endpointDisabled, setEndpointDisabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced fetch napovedy. Pri kazde zmene `value` resetujeme timer a po
  // 250ms naciatame.
  useEffect(() => {
    if (endpointDisabled || disabled) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setSuggestions([]);
      return;
    }

    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await api<string[]>(
            `/material/suggest?q=${encodeURIComponent(trimmed)}&limit=20`,
          );
          setSuggestions(Array.isArray(res) ? res : []);
        } catch (err) {
          if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
            // Backend endpoint jeste neni nasazen — vypneme napovedu.
            setEndpointDisabled(true);
          }
          setSuggestions([]);
        }
      })();
    }, 250);

    return () => window.clearTimeout(handle);
  }, [value, endpointDisabled, disabled]);

  // Click-away handler — kdyz user klikne mimo, zavreme dropdown.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const showDropdown = open && !endpointDisabled && suggestions.length > 0;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="block w-full rounded-sm px-2 py-1.5 text-left hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
